import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Mail, Phone, Facebook, MapPin } from "lucide-react";
import { useContenido } from "@/context/ContenidoContext";
import { HORARIO_TEXTO } from "@/lib/horario";

export default function Footer() {
    const { t } = useTranslation();
    const { config } = useContenido();
    const year = new Date().getFullYear();
    const horarios = config?.horarios?.length ? config.horarios : HORARIO_TEXTO;
    const telefono = config?.telefono || "925 48 46 33";
    const telLink = config?.telefono_link || "+34925484633";
    const email = config?.email || "restaurantemuseoteneria@gmail.com";
    const facebook =
        config?.facebook_url ||
        "https://www.facebook.com/Restaurante-Museo-Tener%C3%ADa-386110368612753";
    const direccion = config?.direccion || "Plaza del Mercado 3, Ocaña · 45300 Toledo";
    const mapQuery =
        config?.google_maps_query ||
        "Restaurante Museo Tenería Plaza del Mercado 3 Ocaña";

    return (
        <footer className="relative border-t border-gold/15 bg-bg-alt overflow-hidden">
            <div className="absolute inset-0 paper-grain pointer-events-none" />
            <div className="relative max-w-[1500px] mx-auto px-6 md:px-12 py-24">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="text-center mb-20"
                >
                    <p className="font-editorial italic text-xs tracking-[0.4em] uppercase text-gold mb-6">
                        — Restaurante · Museo —
                    </p>
                    <img
                        src="/logo-teneria.jpg"
                        alt="Logo Restaurante Museo Tenería"
                        className="mx-auto w-24 h-24 md:w-28 md:h-28 rounded-full object-cover bg-ivory border border-gold/30 mb-6"
                    />
                    <span className="font-display italic text-4xl md:text-5xl text-gold leading-none">
                        Restaurante Museo Tenería
                    </span>
                    <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-stone mt-6">
                        Anno · MCDLXX
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-x-14 gap-y-14">
                    <div>
                        <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold/70 mb-5">
                            {t("footer.address")}
                        </p>
                        <p className="font-body text-ivory leading-relaxed text-base whitespace-pre-line">
                            {direccion}
                        </p>
                        <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-gold mt-5 text-sm link-underline font-editorial italic"
                            data-testid="footer-map-link"
                            data-cursor="hover"
                        >
                            <MapPin size={14} /> {t("footer.viewMap")}
                        </a>
                    </div>

                    <div>
                        <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold/70 mb-5">
                            {t("footer.schedule")}
                        </p>
                        <ul className="font-body text-sm space-y-2">
                            {horarios.map((h) => (
                                <li key={h.dia} className="flex justify-between gap-4">
                                    <span className="text-ivory">{h.dia}</span>
                                    <span
                                        className={
                                            h.cerrado
                                                ? "text-oxblood italic"
                                                : "text-stone"
                                        }
                                    >
                                        {h.horas}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold/70 mb-5">
                            {t("footer.contact")}
                        </p>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <a
                                    href={`tel:${telLink}`}
                                    className="inline-flex items-center gap-2 text-ivory hover:text-gold transition-colors"
                                    data-testid="footer-phone-link"
                                    data-cursor="hover"
                                >
                                    <Phone size={14} className="text-gold" /> {telefono}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={`mailto:${email}`}
                                    className="inline-flex items-center gap-2 text-ivory hover:text-gold transition-colors break-all"
                                    data-testid="footer-email-link"
                                    data-cursor="hover"
                                >
                                    <Mail size={14} className="text-gold" /> {email}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={facebook}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 text-ivory hover:text-gold transition-colors"
                                    data-testid="footer-facebook-link"
                                    data-cursor="hover"
                                >
                                    <Facebook size={14} className="text-gold" /> Facebook
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-20 pt-10 border-t border-gold/15 flex flex-col md:flex-row items-center justify-between gap-6">
                    <nav className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[0.7rem] uppercase tracking-[0.3em] font-body text-stone">
                        {["gastronomia", "museo", "eventos", "premios", "blog", "reservas", "contacto"].map(
                            (slug) => (
                                <Link
                                    key={slug}
                                    to={`/${slug}`}
                                    className="hover:text-gold transition-colors"
                                    data-cursor="hover"
                                >
                                    {slug === "blog"
                                        ? "Diario"
                                        : slug.charAt(0).toUpperCase() + slug.slice(1)}
                                </Link>
                            ),
                        )}
                    </nav>
                </div>

                <div className="mt-10 text-center font-editorial italic text-xs text-stone/70 tracking-wider">
                    © {year} Restaurante Museo Tenería · Edificio del siglo XV · Antigua Judería
                    de Ocaña
                </div>
            </div>
        </footer>
    );
}
